import React, { useState } from 'react';
import Box from '@amzn/awsui-components-react/polaris/box';
import SpaceBetween from '@amzn/awsui-components-react/polaris/space-between';
import Button, { ButtonProps } from '@amzn/awsui-components-react/polaris/button';
import Container from '@amzn/awsui-components-react/polaris/container';
import Header from '@amzn/awsui-components-react/polaris/header';
import Alert from '@amzn/awsui-components-react/polaris/alert';
import Link from '@amzn/awsui-components-react/polaris/link';
import ExpandableSection from '@amzn/awsui-components-react/polaris/expandable-section';
import TextContent from '@amzn/awsui-components-react/polaris/text-content';

interface Step {
  text: string;
  yes?: string;
  no?: string;
  action?: boolean;
  link?: string;
  linkText?: string;
  additionalInfo?: string[];

}

interface Steps {
  [key: string]: Step;
}

const CustomerDecisionTree: React.FC = () => {
  const [currentStep, setCurrentStep] = useState('store_configurations');
  const [history, setHistory] = useState<{ question: string; answer: string }[]>([]);

  const steps: Steps = {
    store_configurations: {
      text: 'Have the following store configurations been created for this site? (Warehouse Id, IOG, Marketplace, Merchant ID, FMID (Fulfillment Manager ID), Lister ID, DSID (Demand Stream ID))',
      yes: 'confirm_iap',
      no: 'confirm_iap',
    },
    confirm_iap: {
      text: 'Please confirm IAP status in IPC. Does the store have IAP?',
      yes: 'create_configurations',
      no: 'end',
      link: 'https://ipc.scoping.selection.amazon.dev/iap',
      linkText: 'Confirm in IPC',
    },
    create_configurations: {
      text: 'Create the missing store configurations. Have all configurations been created?',
      yes: 'validate_mappings',
      no: 'end',
    },
    validate_mappings: {
      text: 'Validate these mappings are correct with ALM/Libby manually. Are the mappings correct?',
      yes: 'check_required_info',
      no: 'correct_mappings',
    },
    correct_mappings: {
      text: 'Correct these mappings are correct with ALM/Libby manually. Are the mappings correct now?',
      yes: 'check_required_info',
      no: 'end',
    },
    check_required_info: {
      text: 'Do you have all the required information? (FGID, DSID, Site launch date, Forecast date, Test order ID (optional), FC, Fresh Delivery Region, Forecasting Group ID, Marketplace, Merchant ID, IAP, Launch Date, Display Label, Location, Blaine Need By Date)',
      yes: 'confirm_fgid',
      no: 'gather_required_info',
    },
    gather_required_info: {
      text: 'Gather all required information before proceeding. Do you have all the information now?',
      yes: 'confirm_fgid',
      no: 'end',
    },
    confirm_fgid: {
      text: 'Is the FGID provided by the Odyssey team?',
      yes: 'confirm_dsid',
      no: 'get_fgid',
    },
    get_fgid: {
      text: 'Get the FGID from the Odyssey team. Do you have it now?',
      yes: 'confirm_dsid',
      no: 'end',
    },
    confirm_dsid: {
      text: 'Is the DSID provided by the Odyssey team?',
      yes: 'confirm_forecast_date',
      no: 'get_dsid',
    },
    get_dsid: {
      text: 'Get the DSID from the Odyssey team. Do you have it now?',
      yes: 'confirm_forecast_date',
      no: 'end',
    },
    confirm_forecast_date: {
      text: 'Is the forecast date set to T-4W from launch?',
      yes: 'confirm_blaine_date',
      no: 'set_forecast_date',
    },
    set_forecast_date: {
      text: 'Set the forecast date to T-4W from launch. Is it set now?',
      yes: 'confirm_blaine_date',
      no: 'end',
    },
    confirm_blaine_date: {
      text: 'Is the Blaine Need By Date set to T-10W before launch?',
      yes: 'cut_sim',
      no: 'set_blaine_date',
    },
    set_blaine_date: {
      text: 'Set the Blaine Need By Date to T-10W before launch. Is it set now?',
      yes: 'cut_sim',
      no: 'end',
    },
    cut_sim: {
      text: 'Cut a SIM to SCOT Hermes team to enable bootstrap forecasts?',
      yes: 'verify_scot',
      no: 'end',
      link: 'https://t.corp.amazon.com/P126776006',
      linkText: 'View sample SIM ticket',
    },
    verify_scot: {
      text: 'Verify SCOT bootstrap forecasts are successful for this site. Are they successful?',
      yes: 'verify_gsc',
      no: 'debug_scot',
      link: 'https://scot-forecasting-portal.amazon.com', // Replace with actual URL
      linkText: 'SCOT Forecasting Portal',
      additionalInfo: [
        'Navigate to the SCOT forecasting portal (if needed, request access here).',
        'Provide the following:',
        '   - FGID (for a site that has been bootstrapped)',
        '   - ASIN (any ASIN in this given site)',
        'Hit "Search"',
        'Verify that all P50/P70/P90 forecasts for both System & Final have forecast value for each of the next 12 weeks.',
        '   -   If you do not see the above, this may be due to propagation delays (up to 24 hours from the bootstrap completion).',
        '   - If delays persist, engage with the Hermes on-call team.'
      ]
    },
    debug_scot: {
      text: 'Debug issues blocking delivery of the forecast (e.g., missing configurations in Scoping). Issues resolved?',
      yes: 'verify_scot',
      no: 'end',
    },
    verify_gsc: {
      text: 'Verify GSC forecasts are being applied for this site. Are they applied?',
      yes: 'success',
      no: 'debug_gsc',
    },
    debug_gsc: {
      text: 'Engage with GSC ASIN Forecasting on-call team. Issues resolved?',
      yes: 'verify_gsc',
      no: 'end',
    },
    success: {
      text: 'Process completed successfully!',
      action: true,
    },
    end: {
      text: 'Process ended. Please review and start over if needed.',
      action: true,
    },
  };

  const handleAnswer = (answer: 'yes' | 'no') => {
    const currentStepData = steps[currentStep];
    setHistory([...history, { question: currentStepData.text, answer }]);

    if (answer === 'yes' && currentStepData.yes) {
      setCurrentStep(currentStepData.yes);
    } else if (answer === 'no' && currentStepData.no) {
      setCurrentStep(currentStepData.no);
    } else {
      setCurrentStep('end');
    }
  };

  const resetProcess = () => {
    setCurrentStep('store_configurations');
    setHistory([]);
  };

  const handleConfirmClick: ButtonProps['onClick'] = ({ detail }) => {
    const currentStepData = steps[currentStep];
    if (currentStepData.link) {
      window.open(currentStepData.link, '_blank');
    }
  };

  const currentStepData = steps[currentStep];

  return (
    <Container>
      <SpaceBetween size="l">
        <Header variant="h1">Site Launch Rediness Wizard</Header>
        <Box>
          <SpaceBetween size="l">
            <Header variant="h2">{currentStepData.text}</Header>
            {currentStepData.additionalInfo && (
              <ExpandableSection headerText="Additional Information">
                <Box padding="s">
                  <TextContent>
                    <ul>
                      {currentStepData.additionalInfo.map((info, index) => (
                        <li key={index}>{info}</li>
                      ))}
                    </ul>
                  </TextContent>
                </Box>
              </ExpandableSection>
            )}
            {currentStepData.link && (
              <Box padding="s">
                <Link href={currentStepData.link} external variant="primary">
                  {currentStepData.linkText || 'More Information'}
                </Link>
              </Box>
            )}
            {!currentStepData.action ? (
              <SpaceBetween size="xs" direction="horizontal">
                <Button onClick={() => handleAnswer('yes')} variant="primary">
                  Yes
                </Button>
                <Button onClick={() => handleAnswer('no')} variant="normal">
                  No
                </Button>
              </SpaceBetween>
            ) : (
              <Button onClick={resetProcess} variant="primary">
                Start Over
              </Button>
            )}
          </SpaceBetween>
        </Box>
        {history.length > 0 && (
          <Box>
            <Header variant="h3">History</Header>
            <SpaceBetween size="s">
              {history.map((item, index) => (
                <Alert key={index} type="info">
                  {item.question} - {item.answer}
                </Alert>
              ))}
            </SpaceBetween>
          </Box>
        )}
      </SpaceBetween>
    </Container>
  );
};

export default CustomerDecisionTree;
